import React, { useState } from 'react';

interface PriceData {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

interface CandlestickChartProps {
  data: PriceData[];
  symbol: string;
  changePointDate?: string;
}

const CandlestickChart: React.FC<CandlestickChartProps> = ({
  data,
  symbol,
  changePointDate
}) => {
  // ツールチップ用のstate
  const [tooltip, setTooltip] = useState<{
    show: boolean;
    x: number;
    y: number;
    data: PriceData | null;
  }>({ show: false, x: 0, y: 0, data: null });

  // コンテナ幅を取得するためのref（Hooksは早期returnの前に呼び出す必要がある）
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = React.useState(1200);

  React.useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.offsetWidth - 40); // padding分を引く
      }
    };
    updateWidth();
    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  // データチェック（Hooksの後に配置）
  if (!data || data.length === 0) {
    return (
      <div style={{ height: '400px', width: '100%', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center', color: '#6c757d' }}>
          <div>📊 データを読み込み中...</div>
          <div style={{ fontSize: '12px', marginTop: '10px' }}>株価データが取得できませんでした</div>
        </div>
      </div>
    );
  }

  // データから価格の範囲を計算
  const prices = data.map(d => [d.open, d.high, d.low, d.close]).flat();
  const minPrice = Math.min(...prices);
  const maxPrice = Math.max(...prices);
  const priceRange = maxPrice - minPrice;

  // データを時系列順にソート（古い順）
  const sortedData = [...data].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

  // SVGでシンプルなローソク足チャートを描画
  const chartWidth = containerWidth;
  const chartHeight = 320; // 高さを少し拡大
  const padding = 60; // 左パディング（価格ラベル用）
  const rightPadding = 30; // 右パディング
  const bottomPadding = 60; // 下パディング（日付ラベル用）
  const topPadding = 30; // 上パディング
  
  const candleSpacing = 4; // ローソク足間の隙間
  const candleWidth = Math.max(4, (chartWidth - padding - rightPadding) / sortedData.length - candleSpacing);
  
  const priceToY = (price: number) => {
    return chartHeight - bottomPadding - ((price - minPrice) / priceRange) * (chartHeight - topPadding - bottomPadding);
  };

  const changePointIndex = changePointDate ? sortedData.findIndex(d => d.date === changePointDate) : -1;

  // ツールチップハンドラー
  const showTooltip = (event: React.MouseEvent, priceData: PriceData) => {
    const parentRect = event.currentTarget.closest('svg')?.getBoundingClientRect();
    if (parentRect) {
      setTooltip({
        show: true,
        x: event.clientX - parentRect.left,
        y: event.clientY - parentRect.top - 10,
        data: priceData
      });
    }
  };

  const hideTooltip = () => {
    setTooltip({ show: false, x: 0, y: 0, data: null });
  };

  return (
    <div ref={containerRef} style={{ height: '440px', width: '100%', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px', padding: '20px' }}>
      <div style={{ textAlign: 'center', marginBottom: '15px' }}>
        <h4 style={{ color: '#2c3e50', marginBottom: '10px' }}>{symbol} - ローソク足チャート</h4>
        <div style={{ fontSize: '14px', color: '#6c757d' }}>
          期間: {sortedData[0]?.date} ～ {sortedData[sortedData.length - 1]?.date} ({sortedData.length}日間)
        </div>
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'center',
        backgroundColor: 'white',
        border: '1px solid #dee2e6',
        borderRadius: '4px',
        padding: '10px',
        overflow: 'hidden'
      }}>
        <svg width={chartWidth} height={chartHeight} style={{ backgroundColor: 'white', display: 'block' }}>
          {/* 価格軸 */}
          <text x="5" y={topPadding + 5} fontSize="16" fill="#666" fontWeight="600">${maxPrice.toFixed(2)}</text>
          <text x="5" y={chartHeight - bottomPadding + 15} fontSize="16" fill="#666" fontWeight="600">${minPrice.toFixed(2)}</text>
          <line x1={padding} y1={topPadding} x2={padding} y2={chartHeight - bottomPadding} stroke="#ddd" strokeWidth="1" />
          <line x1={padding} y1={chartHeight - bottomPadding} x2={chartWidth - rightPadding} y2={chartHeight - bottomPadding} stroke="#ddd" strokeWidth="1" />
          
          {/* 変化点の縦線 */}
          {changePointIndex >= 0 && (
            <line 
              x1={padding + changePointIndex * (candleWidth + candleSpacing)} 
              y1={topPadding} 
              x2={padding + changePointIndex * (candleWidth + candleSpacing)} 
              y2={chartHeight - bottomPadding} 
              stroke="#e74c3c" 
              strokeWidth="2" 
              strokeDasharray="5,5"
            />
          )}
          
          {/* ローソク足 */}
          {sortedData.map((item, index) => {
            const x = padding + index * (candleWidth + candleSpacing);
            const openY = priceToY(item.open);
            const closeY = priceToY(item.close);
            const highY = priceToY(item.high);
            const lowY = priceToY(item.low);
            
            const isUp = item.close >= item.open;
            const bodyColor = isUp ? '#e74c3c' : '#0066cc'; // 赤=上昇、青=下降（日本式）
            const bodyHeight = Math.abs(closeY - openY);
            const bodyY = Math.min(openY, closeY);
            
            return (
              <g key={index}>
                {/* High-Low線 */}
                <line 
                  x1={x + candleWidth / 2} 
                  y1={highY} 
                  x2={x + candleWidth / 2} 
                  y2={lowY} 
                  stroke={bodyColor} 
                  strokeWidth="1" 
                />
                {/* ローソク足の本体 */}
                <rect 
                  x={x} 
                  y={bodyY} 
                  width={candleWidth} 
                  height={Math.max(bodyHeight, 1)} 
                  fill={bodyColor} 
                  stroke={bodyColor} 
                  strokeWidth="1"
                  style={{ cursor: 'crosshair' }}
                  onMouseEnter={(e) => showTooltip(e, item)}
                  onMouseLeave={hideTooltip}
                />
              </g>
            );
          })}
          
          {/* 変化点のマーク */}
          {changePointIndex >= 0 && (
            <>
              <circle
                cx={padding + changePointIndex * (candleWidth + candleSpacing) + candleWidth / 2}
                cy={priceToY(sortedData[changePointIndex].close)}
                r="5"
                fill="#e74c3c"
              />
              <text
                x={padding + changePointIndex * (candleWidth + candleSpacing) + candleWidth / 2}
                y={topPadding - 5}
                fontSize="16"
                fill="#e74c3c"
                fontWeight="600"
                textAnchor="middle"
              >
                変化点
              </text>
            </>
          )}
          
          {/* 日付ラベル（毎日表示、90度回転） */}
          {sortedData.map((item, index) => {
            const x = padding + index * (candleWidth + candleSpacing) + candleWidth / 2;
            const y = chartHeight - bottomPadding + 50; // 横軸線から十分離す
            // MM-DD形式で表示（先頭0を削除）
            const dateStr = item.date.substring(5); // "2025-09-12" → "09-12"
            const [month, day] = dateStr.split('-');
            const dateLabel = `${parseInt(month)}-${parseInt(day)}`; // "09-12" → "9-12"
            
            return (
              <text
                key={`date-label-${index}`}
                x={x}
                y={y}
                fontSize="16"
                fill="#666"
                fontWeight="600"
                textAnchor="start"
                transform={`rotate(-90, ${x}, ${y})`}
              >
                {dateLabel}
              </text>
            );
          })}
          
          {/* ツールチップ */}
          {tooltip.show && tooltip.data && (
            <g style={{ pointerEvents: 'none' }}>
              {/* ツールチップ背景 */}
              <rect
                x={tooltip.x - 80}
                y={tooltip.y - 105}
                width="160"
                height="120"
                fill="rgba(0, 0, 0, 0.75)"
                stroke="#ccc"
                strokeWidth="1"
                rx="5"
                ry="5"
                style={{ pointerEvents: 'none' }}
              />
              {/* ツールチップ内容 */}
              <text x={tooltip.x} y={tooltip.y - 82} fontSize="16" fill="white" textAnchor="middle" fontWeight="bold" style={{ pointerEvents: 'none' }}>
                {tooltip.data.date}
              </text>
              <text x={tooltip.x} y={tooltip.y - 62} fontSize="15" fill="white" textAnchor="middle" style={{ pointerEvents: 'none' }}>
                始値: ${tooltip.data.open.toFixed(2)}
              </text>
              <text x={tooltip.x} y={tooltip.y - 46} fontSize="15" fill="white" textAnchor="middle" style={{ pointerEvents: 'none' }}>
                高値: ${tooltip.data.high.toFixed(2)}
              </text>
              <text x={tooltip.x} y={tooltip.y - 30} fontSize="15" fill="white" textAnchor="middle" style={{ pointerEvents: 'none' }}>
                安値: ${tooltip.data.low.toFixed(2)}
              </text>
              <text x={tooltip.x} y={tooltip.y - 14} fontSize="15" fill="white" textAnchor="middle" style={{ pointerEvents: 'none' }}>
                終値: ${tooltip.data.close.toFixed(2)}
              </text>
              <text x={tooltip.x} y={tooltip.y + 2} fontSize="15" fill="white" textAnchor="middle" style={{ pointerEvents: 'none' }}>
                出来高: {tooltip.data.volume.toLocaleString()}
              </text>
            </g>
          )}
        </svg>
      </div>
      
    </div>
  );
};

export default CandlestickChart;